import { useState } from "react";
import { Bike, ChevronDown, Loader2, X, CheckCircle2 } from "lucide-react";
import { BIKES, getBikeById } from "../lib/bikes";

/**
 * BikePicker
 * Lets the user choose a bike. On selection, the matching .hex firmware is
 * fetched from /firmware/<file>, converted into a File object, and handed off
 * to the parent via onFirmwareLoaded(file, error).
 */
export const BikePicker = ({
    selectedBikeId,
    onBikeSelected,
    onFirmwareLoaded,
    onClear,
    disabled,
    loaded,
}) => {
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const selectedBike = getBikeById(selectedBikeId);

    const pickBike = async (bike) => {
        setOpen(false);
        if (!bike) return;
        onBikeSelected(bike.id);
        setLoading(true);
        try {
            // Files in /public are served from the site root.
            const res = await fetch(`${process.env.PUBLIC_URL || ""}/firmware/${bike.file}`, {
                cache: "no-store",
            });
            if (!res.ok) {
                throw new Error(
                    res.status === 404
                        ? `Firmware not available yet for ${bike.name}. Please contact support.`
                        : `Failed to load firmware (HTTP ${res.status})`,
                );
            }
            const text = await res.text();
            // Quick sanity check: must look like an Intel HEX file. Some dev
            // servers fall back to serving index.html with 200 OK when a file
            // is missing, so we treat a non-HEX response as "not available".
            if (!text.trimStart().startsWith(":")) {
                throw new Error(
                    `Firmware for ${bike.name} is not available yet. Please contact support.`,
                );
            }
            const blob = new Blob([text], { type: "text/plain" });
            const file = new File([blob], bike.file, { type: "text/plain" });
            onFirmwareLoaded(file, null);
        } catch (e) {
            onFirmwareLoaded(null, e.message || "Failed to load firmware");
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setOpen(false);
        onClear();
    };

    // Selected & loaded state
    if (selectedBike && loaded && !loading) {
        return (
            <div
                data-testid="bike-selected"
                className="border border-[#27272a] bg-[#0d0d0e] p-5 flex items-start gap-4"
            >
                <div className="w-10 h-10 flex items-center justify-center bg-[#10B981]/10 border border-[#10B981]/40 shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-mono text-[#ededed] truncate">
                        {selectedBike.name}
                    </div>
                    <div className="text-xs font-mono text-[#a1a1aa] mt-1 uppercase tracking-[0.15em]">
                        firmware ready
                    </div>
                </div>
                {!disabled && (
                    <button
                        type="button"
                        data-testid="bike-clear"
                        onClick={handleClear}
                        className="text-[#a1a1aa] hover:text-[#ef4444] transition-colors p-1"
                        aria-label="Change bike"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="relative">
            <button
                type="button"
                data-testid="bike-picker-trigger"
                disabled={disabled || loading}
                onClick={() => setOpen((v) => !v)}
                className={`w-full border-2 border-dashed p-5 flex items-center gap-4 text-left transition-all ${
                    disabled || loading
                        ? "border-[#27272a] bg-[#0d0d0e] opacity-60 cursor-not-allowed"
                        : open
                          ? "border-[#F59E0B] bg-[#F59E0B]/5"
                          : "border-[#27272a] bg-[#0d0d0e] hover:border-[#F59E0B] hover:bg-[#F59E0B]/5 cursor-pointer"
                }`}
            >
                <div className="w-10 h-10 flex items-center justify-center border border-[#F59E0B]/50 bg-[#F59E0B]/10 shrink-0">
                    {loading ? (
                        <Loader2 className="w-5 h-5 text-[#F59E0B] animate-spin" />
                    ) : (
                        <Bike className="w-5 h-5 text-[#F59E0B]" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#ededed] font-medium">
                        {loading
                            ? `Loading ${selectedBike?.name || "firmware"}…`
                            : selectedBike
                              ? selectedBike.name
                              : "Select your bike"}
                    </div>
                    <div className="text-xs text-[#a1a1aa] font-mono mt-1 uppercase tracking-[0.15em]">
                        {loading
                            ? "fetching firmware"
                            : "firmware loads automatically"}
                    </div>
                </div>
                <ChevronDown
                    className={`w-4 h-4 text-[#a1a1aa] shrink-0 transition-transform ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </button>

            {open && !disabled && !loading && (
                <div
                    data-testid="bike-picker-menu"
                    className="absolute z-30 mt-2 w-full border border-[#27272a] bg-[#0d0d0e] shadow-xl max-h-72 overflow-y-auto"
                >
                    {BIKES.map((bike) => {
                        const isSelected = bike.id === selectedBikeId;
                        return (
                            <button
                                key={bike.id}
                                type="button"
                                data-testid={`bike-option-${bike.id}`}
                                onClick={() => pickBike(bike)}
                                className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-[#1a1a1c] last:border-b-0 transition-colors ${
                                    isSelected
                                        ? "bg-[#F59E0B]/10 text-[#F59E0B]"
                                        : "text-[#ededed] hover:bg-[#F59E0B]/5 hover:text-[#F59E0B]"
                                }`}
                            >
                                <Bike className="w-4 h-4 shrink-0 opacity-70" />
                                <span className="text-sm font-mono">
                                    {bike.name}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
